/**
 * Raw-DB-row → operator-facing shape mappers.
 *
 * Most of the API returns clean camelCase DTOs. A handful of endpoints leak the
 * schema instead, and every one of them is on a money path, so the translation
 * has to live in exactly one place:
 *
 *   - `GET /transactions/:id` returns raw `transaction_items` /
 *     `transaction_tenders` rows (`productNameSnapshot`, `unitPriceCents`,
 *     `tenderType`, …). Rendering those straight into a receipt template shows
 *     an empty receipt for a transaction that is actually fine — the failure
 *     mode that produced this file in the first place (TASK-0077).
 *   - `GET /admin/recovery[/:id]` returns `cartJson` / `appliedTendersJson` as
 *     opaque JSON *strings* written by the Rust register, so keys are
 *     snake_case (with camelCase aliases tolerated by the API's own parser).
 *     A recovery cart is what a manager reads before asserting that money
 *     changed hands, so it is parsed defensively and a malformed snapshot is
 *     reported rather than silently rendered as an empty cart.
 *
 * Rules that apply to everything here:
 *   - Money is integer cents. Nothing in this file divides by 100.
 *   - Postgres `numeric` arrives as a string; quantities are coerced with
 *     `Number()`, never trusted to already be numeric.
 *   - JSONB `meta` blobs are snake_case inside (`auth_code`, `entry_method`,
 *     `recovery_id`, `admin_user_id`, `last4`, `change`). camelCase aliases are
 *     accepted because older rows and hand-written payloads carry both.
 */

import type { TransactionDetail, TransactionLineItem, TransactionTender } from './types';

/* ── Primitive coercions ──────────────────────────────────────────────────── */

type Raw = Record<string, unknown>;

function asRecord(value: unknown): Raw {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Raw)
    : {};
}

/** Coerce to a finite number. pg `numeric` arrives as a string. */
function toNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

function toNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = toNumber(value, Number.NaN);
  return Number.isFinite(parsed) ? parsed : null;
}

function toStringOrNull(value: unknown): string | null {
  if (typeof value === 'string' && value !== '') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function toId(value: unknown): string | number | undefined {
  if (typeof value === 'string' || typeof value === 'number') return value;
  return undefined;
}

/**
 * UUID shape check. Several routes take a UUID path param and answer a
 * malformed one with a 500 rather than a 404, so screens validate before they
 * fetch and render an inline "unavailable" instead of an error boundary.
 */
export function isUuid(value: string | null | undefined): boolean {
  if (!value) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value.trim()
  );
}

/* ── Transactions ─────────────────────────────────────────────────────────── */

/**
 * One raw `transaction_items` row → a receipt line.
 *
 *   productNameSnapshot → description
 *   unitPriceCents      → unitPrice   (cents)
 *   extPriceCents       → total       (cents)
 *   discountCents       → discount    · taxCents → tax
 *   scannedBarcode      → barcode
 *   quantity (numeric string) → quantity (number)
 *
 * Legacy-synced rows carry no department name and no void flag; both default
 * rather than render as `undefined`.
 */
export function mapTransactionItem(raw: unknown): TransactionLineItem {
  const row = asRecord(raw);
  const rawLineType = toStringOrNull(row.lineType);
  const lineType =
    rawLineType === 'sale' ||
    rawLineType === 'return' ||
    rawLineType === 'void' ||
    rawLineType === 'coupon'
      ? rawLineType
      : null;

  return {
    id: toId(row.id),
    description: toStringOrNull(row.productNameSnapshot ?? row.description) ?? '',
    quantity: toNumber(row.quantity, 1),
    unitPrice: toNumber(row.unitPriceCents ?? row.unitPrice),
    total: toNumber(row.extPriceCents ?? row.total),
    discount: toNumber(row.discountCents ?? row.discount),
    tax: toNumber(row.taxCents ?? row.tax),
    barcode: toStringOrNull(row.scannedBarcode ?? row.barcode),
    // The register records a PLU entry in `scannedBarcode` and flags it via
    // `legacyType`; only then is the code a PLU rather than a barcode.
    plu: row.legacyType === 'plu' ? toStringOrNull(row.scannedBarcode) : null,
    departmentName: toStringOrNull(row.departmentName),
    isVoided: Boolean(row.isVoided ?? false),
    lineType,
  };
}

/**
 * One raw `transaction_tenders` row → a receipt tender.
 *
 *   tenderType → type · amount stays cents · reference passes through
 *   meta.last4        → last4        (captured by the card reader)
 *   meta.change       → change       (cash tenders with change due)
 *   meta.auth_code    → authCode     · meta.entry_method → entryMethod
 *   meta.recovery_id  → recoveryId   · meta.admin_user_id → recoveryAdminUserId
 *   meta.source       → recoverySource · meta.reason → recoveryReason
 */
export function mapTransactionTender(raw: unknown): TransactionTender {
  const row = asRecord(raw);
  const meta = asRecord(row.meta);
  const change = meta.change ?? row.change;

  return {
    id: toId(row.id),
    type: toStringOrNull(row.tenderType ?? row.type) ?? 'unknown',
    amount: toNumber(row.amount),
    reference: toStringOrNull(row.reference),
    last4: toStringOrNull(meta.last4 ?? row.last4),
    change: change === null || change === undefined ? null : toNumber(change),
    recoverySource: toStringOrNull(meta.source),
    recoveryId: toStringOrNull(meta.recovery_id ?? meta.recoveryId),
    recoveryReason: toStringOrNull(meta.reason),
    recoveryAdminUserId: toNumberOrNull(meta.admin_user_id ?? meta.adminUserId),
    authCode: toStringOrNull(meta.auth_code ?? meta.authCode),
    entryMethod: toStringOrNull(meta.entry_method ?? meta.entryMethod),
  };
}

/**
 * Over-return flags: one row per sync event that pushed a receipt past the
 * quantity it originally sold. Not in `types.ts` because only the receipt
 * screen consumes them.
 */
export interface TransactionOverReturnFlag {
  id: number | string;
  originalTransactionId: string;
  returnTransactionId: string | null;
  productId: number | null;
  soldQty: number | null;
  returnedQty: number | null;
  detectedAt: string | null;
}

function mapOverReturnFlag(raw: unknown): TransactionOverReturnFlag {
  const row = asRecord(raw);
  return {
    id: toId(row.id) ?? '',
    originalTransactionId: toStringOrNull(row.originalTransactionId) ?? '',
    returnTransactionId: toStringOrNull(row.returnTransactionId),
    productId: toNumberOrNull(row.productId),
    soldQty: toNumberOrNull(row.soldQty),
    returnedQty: toNumberOrNull(row.returnedQty),
    detectedAt: toStringOrNull(row.detectedAt ?? row.createdAt),
  };
}

/** `GET /transactions/:id` plus the two extras the route returns. */
export type TransactionDetailFull = TransactionDetail & {
  overReturnFlags: TransactionOverReturnFlag[];
};

/**
 * Map the whole `GET /transactions/:id` payload. Scalars pass through (they
 * already match the operator-facing type); `items` and `tenders` go through the
 * per-row mappers. Missing arrays become empty rather than undefined so the
 * receipt renders "no lines" instead of throwing.
 */
export function mapTransactionDetail(raw: unknown): TransactionDetailFull {
  const row = asRecord(raw);
  return {
    ...(row as unknown as TransactionDetail),
    items: Array.isArray(row.items) ? row.items.map(mapTransactionItem) : [],
    tenders: Array.isArray(row.tenders) ? row.tenders.map(mapTransactionTender) : [],
    overReturnFlags: Array.isArray(row.overReturnFlags)
      ? row.overReturnFlags.map(mapOverReturnFlag)
      : [],
  };
}

/** Sum of what was actually handed over, before change. */
export function tenderedTotal(tenders: TransactionTender[]): number {
  return tenders.reduce((sum, t) => sum + t.amount, 0);
}

/** Sum of change given back across every tender that returned any. */
export function changeTotal(tenders: TransactionTender[]): number {
  return tenders.reduce((sum, t) => sum + (t.change ?? 0), 0);
}

/* ── Recovery snapshots ───────────────────────────────────────────────────── */

/**
 * Parsing a snapshot either succeeds or explains itself. A recovery cart that
 * cannot be read must never render as an empty cart — a manager finalizing an
 * "empty" cart for its stored total is exactly the mistake this guards.
 */
export type ParseResult<T> = { ok: true; value: T } | { ok: false; error: string };

export interface RecoveryCartLine {
  /** Position in `cartJson`. This is the `lineIndex` a restock decision keys on. */
  index: number;
  productId: number | null;
  description: string;
  quantity: number;
  /** Integer cents. */
  unitPrice: number;
  /** Integer cents — the extended line total. */
  total: number;
  tax: number;
  discount: number;
  barcode: string | null;
  lineType: 'sale' | 'return';
  ebtEligible: boolean;
}

export interface RecoveryAppliedTender {
  index: number;
  type: string;
  /** Integer cents presented. */
  amount: number;
  /** Integer cents actually applied to the balance — this is what settles it. */
  applied: number;
  change: number;
  authCode: string | null;
  last4: string | null;
  reference: string | null;
  entryMethod: string | null;
}

function parseJsonArray(raw: string, field: string): ParseResult<unknown[]> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw) as unknown;
  } catch {
    return { ok: false, error: `${field} is not valid JSON.` };
  }
  if (!Array.isArray(parsed)) {
    return { ok: false, error: `${field} is not a list of lines.` };
  }
  return { ok: true, value: parsed };
}

/**
 * Parse `suspended_transactions.cartJson`. The register writes snake_case;
 * camelCase aliases are accepted because the API's own parser accepts both.
 */
export function parseRecoveryCart(
  cartJson: string | null | undefined
): ParseResult<RecoveryCartLine[]> {
  if (!cartJson) return { ok: true, value: [] };
  const parsed = parseJsonArray(cartJson, 'The cart snapshot');
  if (!parsed.ok) return parsed;

  const lines: RecoveryCartLine[] = [];
  for (const [index, entry] of parsed.value.entries()) {
    const row = asRecord(entry);
    const description =
      toStringOrNull(
        row.product_name_snapshot ??
          row.productNameSnapshot ??
          row.product_name ??
          row.name
      ) ?? '';
    if (!description) {
      return {
        ok: false,
        error: `Cart line ${index + 1} has no product name; this snapshot cannot be finalized.`,
      };
    }
    const unitPrice = toNumberOrNull(row.unit_price_cents ?? row.unitPriceCents);
    const total = toNumberOrNull(row.ext_price_cents ?? row.extPriceCents);
    if (unitPrice === null || total === null) {
      return {
        ok: false,
        error: `Cart line ${index + 1} is missing its price fields; this snapshot cannot be finalized.`,
      };
    }
    const rawLineType = toStringOrNull(row.line_type ?? row.lineType);

    lines.push({
      index,
      productId: toNumberOrNull(row.product_id ?? row.productId),
      description,
      quantity: toNumber(row.qty, 1),
      unitPrice,
      total,
      tax: toNumber(row.tax_cents ?? row.taxCents),
      discount: toNumber(row.discount_cents ?? row.discountCents),
      barcode: toStringOrNull(row.scanned_barcode ?? row.scannedBarcode),
      lineType: rawLineType === 'return' ? 'return' : 'sale',
      ebtEligible: Boolean(
        row.ebt_eligible ?? row.ebtEligible ?? row.is_ebt ?? row.isEbt
      ),
    });
  }
  return { ok: true, value: lines };
}

/** Parse `suspended_transactions.appliedTendersJson` — money already taken. */
export function parseRecoveryTenders(
  json: string | null | undefined
): ParseResult<RecoveryAppliedTender[]> {
  if (!json) return { ok: true, value: [] };
  const parsed = parseJsonArray(json, 'The applied-tender snapshot');
  if (!parsed.ok) return parsed;

  const tenders: RecoveryAppliedTender[] = [];
  for (const [index, entry] of parsed.value.entries()) {
    const row = asRecord(entry);
    const type = toStringOrNull(row.tender_type ?? row.tenderType);
    const amount = toNumberOrNull(row.amount_cents ?? row.amountCents);
    const applied = toNumberOrNull(row.applied_cents ?? row.appliedCents);
    if (!type || amount === null || applied === null) {
      return {
        ok: false,
        error: `Applied tender ${index + 1} is missing its type or amount; the balance due cannot be trusted.`,
      };
    }
    const maskedPan = toStringOrNull(row.masked_pan ?? row.maskedPan);
    tenders.push({
      index,
      type,
      amount,
      applied,
      change: toNumber(row.change_cents ?? row.changeCents),
      authCode: toStringOrNull(row.auth_code ?? row.authCode),
      last4:
        toStringOrNull(row.last_four ?? row.lastFour) ??
        (maskedPan ? maskedPan.replace(/\D/g, '').slice(-4) || null : null),
      reference: toStringOrNull(row.ref_number ?? row.refNumber ?? row.reference),
      entryMethod: toStringOrNull(row.entry_method ?? row.entryMethod),
    });
  }
  return { ok: true, value: tenders };
}

/** What the finalize tender has to settle: total minus what is already applied. */
export function recoveryBalanceDue(
  totalCents: number,
  applied: RecoveryAppliedTender[]
): number {
  return totalCents - applied.reduce((sum, t) => sum + t.applied, 0);
}
